import { useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOSchema from '../../components/feature/SEOSchema';

export default function TermsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://workflowtools.com';

  const schema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Service — WorkflowTools',
    url: `${siteUrl}/terms`,
    description: 'Read the WorkflowTools terms of service. Understand your rights and obligations when using our document template platform and credit-based services.',
    dateModified: '2025-01-01',
    isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'WorkflowTools' },
    about: { '@type': 'Organization', name: 'WorkflowTools', url: siteUrl },
  }), [siteUrl]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOSchema schema={schema} id="terms-schema" />
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last updated: January 2025</p>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 lg:p-12 space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using WorkFlowTools ("Service"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                WorkFlowTools provides online productivity tools including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>PDF tools (merge, split, compress, convert)</li>
                <li>File conversion services</li>
                <li>Document generation tools</li>
                <li>Business productivity utilities</li>
                <li>AI-powered writing and analysis tools</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To access certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your password</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription Plans</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong className="text-gray-900">Free Plan:</strong> Limited access to basic tools with usage restrictions.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong className="text-gray-900">Pro Plan:</strong> Unlimited access to all tools, AI features, and priority support.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong className="text-gray-900">Business Plan:</strong> Team accounts, admin dashboard, API access, and dedicated support.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Subscription fees are billed in advance on a monthly or annual basis. You may cancel your subscription at any time.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Upload illegal, harmful, or offensive content</li>
                <li>Violate any intellectual property rights</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use the Service for any unlawful purpose</li>
                <li>Distribute malware or viruses</li>
                <li>Abuse or overload our infrastructure</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. File Processing and Storage</h2>
              <p className="text-gray-700 leading-relaxed">
                Files uploaded to our Service are processed and automatically deleted within 24 hours. We do not claim ownership of your files. You retain all rights to your content.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service and its original content, features, and functionality are owned by WorkFlowTools and are protected by international copyright, trademark, and other intellectual property laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                WorkFlowTools shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the Service. We provide the Service "as is" without warranties of any kind.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Refund Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We offer a 14-day money-back guarantee for new subscriptions. Refund requests must be submitted within 14 days of the initial purchase. Refunds are not available for renewals.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to terminate or suspend your account immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes. Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed">
                For questions about these Terms, please contact us:
              </p>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-gray-900 font-medium">Email: legal@workflowtools.com</p>
                <p className="text-gray-900 font-medium">Address: 123 Business Street, San Francisco, CA 94102</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}